import { SEOHead } from "@/components/seo";
import { SmoothLink as Link } from "@/components/navigation";
import { useAnalyticsContext } from "@/components/common/AnalyticsProvider";

import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { BreadcrumbJsonLd, AnswerCapsule, OrganizationJsonLd, TableSchema } from "@/components/seo";
import { PageHeader } from "@/components/shared/PageHeader";
import { COMPANY } from "@/config/company";
import { CTASection } from "@/components/home/CTASection";
import { 
  TrendingDown, 
  DollarSign, 
  CheckCircle,
  ArrowRight,
  Calculator,
  Plane,
  Building2
} from "lucide-react";

interface ProcedureCost {
  name: string;
  slug: string;
  category: string;
  indiaMin: number;
  indiaMax: number;
  usaMin: number;
  usaMax: number;
  ukMin: number;
  ukMax: number;
  duration: string;
  includes: string[];
}

const procedures: ProcedureCost[] = [
  // Dental
  {
    name: "Single Dental Implant",
    slug: "dental-implants",
    category: "dental",
    indiaMin: 400,
    indiaMax: 800,
    usaMin: 3000,
    usaMax: 5000,
    ukMin: 2000,
    ukMax: 3500,
    duration: "5-7 days",
    includes: ["Implant (Nobel/Straumann)", "Crown", "X-rays", "Follow-up"]
  },
  {
    name: "All-on-4 Dental Implants",
    slug: "all-on-4-implants",
    category: "dental",
    indiaMin: 5000,
    indiaMax: 8000,
    usaMin: 25000,
    usaMax: 40000,
    ukMin: 18000,
    ukMax: 30000,
    duration: "7-10 days",
    includes: ["4 Implants per arch", "Fixed bridge", "Temporary teeth", "All scans"]
  },
  {
    name: "Porcelain Veneers (per tooth)",
    slug: "porcelain-veneers",
    category: "dental",
    indiaMin: 200,
    indiaMax: 400,
    usaMin: 1000,
    usaMax: 2500,
    ukMin: 800,
    ukMax: 1500,
    duration: "5-7 days",
    includes: ["E-max/Zirconia veneers", "Prep work", "Custom shade matching"]
  },
  {
    name: "Full Mouth Rehabilitation",
    slug: "smile-makeover",
    category: "dental",
    indiaMin: 3000,
    indiaMax: 8000,
    usaMin: 20000,
    usaMax: 50000,
    ukMin: 15000,
    ukMax: 35000,
    duration: "10-14 days",
    includes: ["Crowns", "Veneers", "Implants as needed", "Whitening"]
  },
  // Cardiac
  {
    name: "Heart Bypass Surgery (CABG)",
    slug: "heart-bypass-cabg",
    category: "cardiac",
    indiaMin: 8000,
    indiaMax: 15000,
    usaMin: 100000,
    usaMax: 150000,
    ukMin: 25000,
    ukMax: 45000,
    duration: "14-21 days",
    includes: ["Surgery", "ICU stay", "Hospital room", "Cardiac rehab start"]
  },
  {
    name: "Angioplasty with Stent",
    slug: "angioplasty",
    category: "cardiac",
    indiaMin: 3000,
    indiaMax: 6000,
    usaMin: 30000,
    usaMax: 50000,
    ukMin: 8000,
    ukMax: 15000,
    duration: "3-5 days",
    includes: ["Drug-eluting stent", "Cathlab", "Hospital stay", "Medications"]
  },
  {
    name: "Valve Replacement",
    slug: "pacemaker-implantation",
    category: "cardiac",
    indiaMin: 8000,
    indiaMax: 12000,
    usaMin: 80000,
    usaMax: 170000,
    ukMin: 20000,
    ukMax: 40000,
    duration: "10-14 days",
    includes: ["Valve (mechanical/bio)", "Surgery", "ICU", "Cardiac monitoring"]
  },
  // Orthopedics
  {
    name: "Total Knee Replacement",
    slug: "knee-replacement",
    category: "orthopedics",
    indiaMin: 6000,
    indiaMax: 10000,
    usaMin: 50000,
    usaMax: 70000,
    ukMin: 12000,
    ukMax: 20000,
    duration: "10-14 days",
    includes: ["Imported implant", "Surgery", "Physiotherapy", "Hospital stay"]
  },
  {
    name: "Total Hip Replacement",
    slug: "hip-replacement",
    category: "orthopedics",
    indiaMin: 7000,
    indiaMax: 12000,
    usaMin: 50000,
    usaMax: 65000,
    ukMin: 12000,
    ukMax: 18000,
    duration: "10-14 days",
    includes: ["Ceramic/Metal implant", "Surgery", "Rehabilitation", "Room"]
  },
  {
    name: "Spine Fusion Surgery",
    slug: "spine-decompression-fusion",
    category: "orthopedics",
    indiaMin: 6000,
    indiaMax: 12000,
    usaMin: 80000,
    usaMax: 150000,
    ukMin: 20000,
    ukMax: 40000,
    duration: "14-21 days",
    includes: ["Implants", "Surgery", "Hospital stay", "Physical therapy"]
  },
  // Oncology
  {
    name: "Bone Marrow Transplant",
    slug: "bone-marrow-transplant",
    category: "oncology",
    indiaMin: 25000,
    indiaMax: 40000,
    usaMin: 350000,
    usaMax: 800000,
    ukMin: 150000,
    ukMax: 250000,
    duration: "60-90 days",
    includes: ["Transplant", "Isolation room", "All medications", "Follow-up"]
  },
  // Fertility
  {
    name: "IVF Treatment (1 cycle)",
    slug: "fertility",
    category: "fertility",
    indiaMin: 3000,
    indiaMax: 5000,
    usaMin: 15000,
    usaMax: 25000,
    ukMin: 5000,
    ukMax: 10000,
    duration: "14-21 days",
    includes: ["Egg retrieval", "Fertilization", "Transfer", "Medications"]
  },
];

const formatPrice = (min: number, max: number) => {
  if (min === max) return `$${min.toLocaleString()}`;
  return `$${min.toLocaleString()} - $${max.toLocaleString()}`;
};

const calculateSavings = (indiaMax: number, usaMax: number) => {
  return Math.round(((usaMax - indiaMax) / usaMax) * 100);
};

export default function CompareCosts() {
  const { trackCTAClick } = useAnalyticsContext();
  const breadcrumbItems = [
    { name: "Home", url: `${COMPANY.website}/` },
    { name: "Cost Comparison", url: `${COMPANY.website}/compare-costs` },
  ];

  // Generate structured data for cost comparison
  const costComparisonSchema = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    name: "Medical Procedure Cost Comparison: India vs USA vs UK",
    description: "Comprehensive cost comparison of medical procedures in India versus USA and UK. Patients save 60-90% on world-class treatment.",
    numberOfItems: procedures.length,
    itemListElement: procedures.map((proc, index) => ({
      "@type": "ListItem",
      position: index + 1,
      item: {
        "@type": "MedicalProcedure",
        name: proc.name,
        procedureType: "https://schema.org/SurgicalProcedure",
        offers: [
          {
            "@type": "Offer",
            name: `${proc.name} in India`,
            priceCurrency: "USD",
            price: proc.indiaMin,
            priceSpecification: {
              "@type": "PriceSpecification",
              minPrice: proc.indiaMin,
              maxPrice: proc.indiaMax,
              priceCurrency: "USD"
            },
            offeredBy: {
              "@type": "MedicalBusiness",
              name: COMPANY.name
            },
            areaServed: "IN"
          }
        ]
      }
    }))
  };

  const categories = [
    { id: "dental", name: "Dental", icon: "🦷" },
    { id: "cardiac", name: "Cardiac", icon: "❤️" },
    { id: "orthopedics", name: "Orthopedics", icon: "🦴" },
    { id: "oncology", name: "Oncology", icon: "🎗️" },
    { id: "fertility", name: "Fertility", icon: "👶" },
  ];

  return (
    <div className="min-h-screen bg-background">
            <SEOHead
        title={"Compare Medical Treatment Costs | India vs USA vs UK | TrustedHeal"}
        description={"Compare medical treatment costs in India vs USA and UK. See how much you can save on dental implants, heart surgery, joint replacement, and more."}
        canonicalPath={"/compare-costs/"}
        ogType="website"
      />

      <OrganizationJsonLd />
      <BreadcrumbJsonLd items={breadcrumbItems} />
      
      {/* Table Schema for Cost Comparison */}
      {categories.map((category) => {
        const categoryProcedures = procedures.filter(p => p.category === category.id);
        if (categoryProcedures.length === 0) return null;
        
        return (
          <TableSchema
            key={`schema-${category.id}`}
            name={`${category.name} Procedure Cost Comparison`}
            description={`Cost comparison of ${category.name.toLowerCase()} procedures in India vs USA vs UK`}
            columns={["Procedure", "India (USD)", "USA (USD)", "UK (USD)", "Savings %", "Duration"]}
            rows={categoryProcedures.map(proc => ({
              "Procedure": proc.name,
              "India (USD)": formatPrice(proc.indiaMin, proc.indiaMax),
              "USA (USD)": formatPrice(proc.usaMin, proc.usaMax),
              "UK (USD)": formatPrice(proc.ukMin, proc.ukMax),
              "Savings %": `${calculateSavings(proc.indiaMax, proc.usaMax)}%`,
              "Duration": proc.duration
            }))}
            about={`Medical tourism cost comparison for ${category.name.toLowerCase()} procedures`}
          />
        );
      })}



      <main className="pt-24">
        {/* Hero Replacement */}
        <div className="container mx-auto px-4">
          <PageHeader 
            title="Medical Procedure Costs: India vs USA vs UK"
            description="Compare real costs for medical procedures. See exactly how much you can save with world-class treatment in India—same quality, 60-90% lower cost."
            badge={
              <span className="inline-block bg-accent/10 border border-accent/20 text-accent px-4 py-1.5 rounded-full text-sm font-medium">
                Transparent Pricing
              </span>
            }
          >
            <div className="flex flex-wrap justify-center gap-6 text-left max-w-2xl mx-auto mt-8">
              <div className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-accent" />
                <span className="text-muted-foreground font-medium">Prices include hospital stay</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-accent" />
                <span className="text-muted-foreground font-medium">No hidden fees</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-accent" />
                <span className="text-muted-foreground font-medium">JCI-accredited hospitals</span>
              </div>
            </div>
          </PageHeader>
        
          <div className="container mx-auto px-4 mb-12">
            <AnswerCapsule
              term="Compare Medical Costs"
              definition="Comprehensive breakdown of medical procedures including Dental Implants, Cardiac Surgery, and Orthopedics. Prices in India are 70-90% lower than US/UK averages."
              statistic="Heart Bypass Savings: $120,000+"
              source="TrustedHeal Verified Network 2025"
            />
          </div>
        
          {/* Summary Stats */}
          <div className="grid md:grid-cols-4 gap-6 max-w-5xl mx-auto mb-20">
            {[
              { value: "60-90%", label: "Average Savings vs USA" },
              { value: "50-80%", label: "Average Savings vs UK" },
              { value: "2M+", label: "International Patients/Year" },
              { value: "40+", label: "Procedures Compared" },
            ].map((stat) => (
              <Card key={stat.label} className="p-6 text-center shadow-sm hover:shadow-md transition-shadow border-border/50">
                <p className="font-display text-3xl font-bold text-primary mb-2">{stat.value}</p>
                <p className="text-sm text-muted-foreground">{stat.label}</p>
              </Card>
            ))}
          </div>
        </div>

        {/* Cost Comparison Tables by Category */}
        {categories.map((category) => {
          const categoryProcedures = procedures.filter(p => p.category === category.id);
          if (categoryProcedures.length === 0) return null;

          return (
            <section key={category.id} className="py-12 border-b border-border last:border-0" id={category.id}>
              <div className="container mx-auto px-4">
                <h2 className="font-display text-2xl md:text-3xl font-bold text-secondary mb-8 flex items-center gap-3">
                  <span className="text-3xl">{category.icon}</span>
                  {category.name} Procedures
                </h2>

                <div className="overflow-x-auto">
                  <table className="w-full bg-card rounded-xl overflow-hidden shadow-soft min-w-[700px]">
                    <thead className="bg-secondary text-primary-foreground">
                      <tr>
                        <th className="px-4 py-4 text-left font-display">Procedure</th>
                        <th className="px-4 py-4 text-right font-display">
                          <span className="flex items-center justify-end gap-1">
                            🇮🇳 India
                          </span>
                        </th>
                        <th className="px-4 py-4 text-right font-display">
                          <span className="flex items-center justify-end gap-1">
                            🇺🇸 USA
                          </span>
                        </th>
                        <th className="px-4 py-4 text-right font-display">
                          <span className="flex items-center justify-end gap-1">
                            🇬🇧 UK
                          </span>
                        </th>
                        <th className="px-4 py-4 text-right font-display">Your Savings</th>
                        <th className="px-4 py-4 text-center font-display">Duration</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      {categoryProcedures.map((proc) => (
                        <tr key={proc.slug} className="hover:bg-muted/50 transition-colors">
                          <td className="px-4 py-4">
                            <Link 
                              to={`/treatments/${proc.category}/${proc.slug}/`}
                              className="font-medium text-secondary hover:text-primary transition-colors"
                            >
                              {proc.name}
                            </Link>
                            <div className="flex flex-wrap gap-1 mt-1">
                              {proc.includes.slice(0, 2).map((item) => (
                                <span key={item} className="text-xs bg-muted px-2 py-0.5 rounded text-muted-foreground">
                                  {item}
                                </span>
                              ))}
                            </div>
                          </td>
                          <td className="px-4 py-4 text-right">
                            <span className="font-bold text-primary text-lg">
                              {formatPrice(proc.indiaMin, proc.indiaMax)}
                            </span>
                          </td>
                          <td className="px-4 py-4 text-right text-muted-foreground">
                            {formatPrice(proc.usaMin, proc.usaMax)}
                          </td>
                          <td className="px-4 py-4 text-right text-muted-foreground">
                            {formatPrice(proc.ukMin, proc.ukMax)}
                          </td>
                          <td className="px-4 py-4 text-right">
                            <span className="inline-flex items-center gap-1 bg-accent/20 text-accent px-3 py-1 rounded-full font-semibold">
                              <TrendingDown className="w-4 h-4" />
                              {calculateSavings(proc.indiaMax, proc.usaMax)}%
                            </span>
                          </td>
                          <td className="px-4 py-4 text-center text-sm text-muted-foreground">
                            {proc.duration}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div className="mt-4 text-right">
                  <Link 
                    to={`/treatments/${category.id}/`}
                    className="text-primary font-medium hover:underline inline-flex items-center gap-1"
                    onClick={() => trackCTAClick(`View All ${category.name}`, 'compare_costs_table', 'link')}
                  >
                    View all {category.name.toLowerCase()} treatments
                    <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
              </div>
            </section>
          );
        })}

        {/* What's Included */}
        <section className="py-16 bg-muted">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <h2 className="font-display text-2xl md:text-3xl font-bold text-secondary mb-8 text-center">
                What's Included in India Prices?
              </h2>
              <div className="grid md:grid-cols-3 gap-6">
                <Card className="p-6 text-center">
                  <Building2 className="w-10 h-10 text-primary mx-auto mb-4" />
                  <h3 className="font-display font-semibold text-secondary mb-2">Hospital Stay</h3>
                  <p className="text-sm text-muted-foreground">
                    Private/semi-private room, nursing care, meals, medications during stay
                  </p>
                </Card>
                <Card className="p-6 text-center">
                  <Calculator className="w-10 h-10 text-primary mx-auto mb-4" />
                  <h3 className="font-display font-semibold text-secondary mb-2">Surgeon & Team</h3>
                  <p className="text-sm text-muted-foreground">
                    Surgeon fees, anesthesiologist, operating room, equipment, implants
                  </p>
                </Card>
                <Card className="p-6 text-center">
                  <Plane className="w-10 h-10 text-primary mx-auto mb-4" />
                  <h3 className="font-display font-semibold text-secondary mb-2">Concierge Support</h3>
                  <p className="text-sm text-muted-foreground">
                    Airport pickup, care coordinator, interpreter, post-op follow-up
                  </p>
                </Card>
              </div>
              <p className="text-center text-muted-foreground mt-6 text-sm">
                * Prices are estimates and may vary based on complexity, hospital choice, and implant type. 
                Get a personalized quote for accurate pricing.
              </p>
            </div>
          </div>
        </section>

        {/* CTA */}
        <CTASection />

        {/* Sources & Citations */}
        <section className="py-12">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <h2 className="font-display text-2xl font-bold text-secondary mb-4">
                Sources & Citations
              </h2>
              <p className="text-sm text-muted-foreground mb-4">
                Prices shown are indicative ranges compiled from public provider references and may vary by hospital,
                case complexity, and implant type. Request a personalized quote for accurate pricing.
              </p>
              <ul className="list-disc pl-6 text-sm text-muted-foreground space-y-2">
                <li>
                  <a
                    href="https://www.scientificdentalclinic.com/dental-implants-cost-in-us-and-india/"
                    target="_blank"
                    rel="noopener noreferrer nofollow"
                    className="text-primary hover:underline"
                  >
                    Example dental implant cost ranges (India vs USA)
                  </a>
                </li>
                <li>
                  <a
                    href="https://www.jointcommissioninternational.org/what-we-offer/accreditation/"
                    target="_blank"
                    rel="noopener noreferrer nofollow"
                    className="text-primary hover:underline"
                  >
                    JCI accreditation overview (Joint Commission International)
                  </a>
                </li>
                <li>
                  <a
                    href="https://nabh.co/faq-nabh/"
                    target="_blank"
                    rel="noopener noreferrer nofollow"
                    className="text-primary hover:underline"
                  >
                    NABH overview and recognition (NABH)
                  </a>
                </li>
              </ul>
            </div>
          </div>
        </section>
      </main>


    </div>
  );
}
